<template>
  <div class="module-launch">
    <header class="top-bar">
      <ul class="top-bar-items">
        <li class="module-li">
          <p
            v-on="['init'].includes(moduleStatus) ? { click: backToModuleList } : {}"
            :class="{
              'module-btn': true,
              disabled: !['init'].includes(moduleStatus),
            }"
          >
            Back
          </p>
        </li>
        <li class="module-li">
          <p
            v-on="['init'].includes(moduleStatus) ? { click: startModule } : {}"
            :class="{
              'module-btn': true,
              disabled: !['init'].includes(moduleStatus),
            }"
          >
            Start Module
          </p>
        </li>
        <li class="module-li">
          <p
            v-on="
              ['running'].includes(moduleStatus)
                ? {
                    click: () => {
                      endPoupShow = true;
                    },
                  }
                : {}
            "
            :class="{
              'module-btn': true,
              disabled: !['running'].includes(moduleStatus),
            }"
          >
            End Module
          </p>
        </li>
        <li class="module-status">
          <span v-html="moduleText" />
          <!-- <span>Module: {{currentModule.name}}</span> -->
        </li>

        <li class="module-li">
          <p class="module-btn icon-btn" v-on="{ click: () => enterFullscreen() }">
            Go Fullscreen
            <inline-svg class="svg-icon" :src="$assets.svg.fullscreen_icon" width="1.2em" height="1.2em" />
          </p>
        </li>
      </ul>
    </header>

    <div class="module-launch-box" ref="moduleBox">
      <div v-if="['loading', 'booting'].includes(moduleStatus)" class="loading-cover">
        <GlobalLoading />
      </div>

      <iframe
        title="Module launching box"
        v-if="['loading', 'booting', 'stopping', 'stopped'].includes(moduleStatus)"
        class="lock-cover"
      />
    </div>

    <div class="no-complete-bg" v-if="endPoupShow">
      <div class="no-complete-box">
        <p class="no-complete-content">
          <span class="highlight">Just a moment</span>

          <span v-if="holdWindow">
            The module isn't finished yet,<br />
            If you continue, you will end the current running module.<br />
          </span>
          <span v-else>
            You are trying to end the module.<br />
            Are you sure you want to leave?
          </span>
        </p>
        <section class="operations">
          <button type="button" class="submit" v-on="['running'].includes(moduleStatus) ? { click: stopModule } : {}">
            Yes
          </button>
          <button
            type="button"
            class="submit"
            @click="
              endMethod = '';
              endPoupShow = false;
              holdWindow = false;
            "
          >
            Cancel
          </button>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeMount, onUnmounted, computed, watch } from 'vue';
import { useRouter, onBeforeRouteLeave } from 'vue-router';

import $assets from '@/assets';
import GlobalLoading from '@/components/common/GlobalLoading.vue';
import ModuleService from '@/services/module';
import useSessionStore from '@/stores/session';
import Logger from '@/utils/logger';
import $path from '@/utils/path';
import $socket from '@/utils/socket';

const props = defineProps({
  topicId: {
    type: String,
    required: true,
  },
  moduleId: {
    type: String,
    required: true,
  },
});

const $router = useRouter();
const SessionStore = useSessionStore();

const currentModule = ref(null);
const moduleBox = ref(null);
const moduleStatus = ref('init'); // init | loading | booting | running | stopping | stopped
const moduleText = ref(null);
const moduleIframe = ref(null);

const holdWindow = ref(false);
const endPoupShow = ref(false);
const endMethod = ref(null);

onBeforeMount(async () => {
  const moduleResults = await ModuleService.getModule(props.moduleId);
  currentModule.value = moduleResults.record;
});

onBeforeRouteLeave(() => {
  if (['init', 'stopped'].includes(moduleStatus.value)) {
    return true;
  }

  endMethod.value = 'route';
  holdWindow.value = true;
  endPoupShow.value = true;

  return false;
});

onUnmounted(() => {
  $socket.close();
});

const moduleUrl = computed(() => {
  return $path.resolveGate(currentModule.value.code_dir);
});

function backToModuleList() {
  $router.push({
    name: 'moduleList',
    params: {
      topicId: props.topicId,
    },
  });
}

/**
 * Socket connection has established
 */
function connected() {
  // create a new session on socket server
  $socket.emit('track_module', {
    hostId: SessionStore.loginUser.id,
    topicId: props.topicId,
    moduleKey: currentModule.value.module_key,
    source: 'facilitator-portal',
    isContinue: moduleStatus.value === 'running',
  });
}
/**
 * A new session has created
 */
function sessionCreated(data) {
  // Update current client's socket id
  if (moduleStatus.value === 'running') Logger.debug('Reconnected to the server');

  Logger.debug(`Session created: ${data.socketId}`);

  if (moduleStatus.value !== 'running') {
    moduleIframe.value.style.opacity = 1;
    moduleStatus.value = 'running';
    moduleText.value = `Running Module: <br />${currentModule.value.name}`;
  }
}

/**
 * The running module has ended
 */
async function sessionRemoved(data) {
  if (data) {
    Logger.debug(`Session removed: ${data.socketId}`);
  }

  // Renew token to avoid timeout
  // await this.$api.put('/facilitator-portal/sessions');

  moduleStatus.value = 'stopped';
  $router.push({ name: 'moduleParticipation' });
}

/**
 * The socket connection was not successfully established
 */
async function socketFailed(err) {
  Logger.debug(`socketFailed: ${err.message}`);

  // The participation form needs to be completed before running another module
  if (err.message === 'ModuleToComplete') {
    // Clear session and direct to participation form
    sessionRemoved();
    return;
  }

  // Reset module data and socket connection
  moduleIframe.value.remove();
  $socket.close();

  // The current running module must be ended before running another
  if (err.message === 'ModuleIsRunning') {
    moduleText.value = 'The module is running from another browser';
    moduleStatus.value = 'init';
    return;
  }

  // Unknow error form server
  moduleText.value = 'Server unknow error';
  moduleStatus.value = 'init';
}

/**
 * Request a module from server to run
 */
async function startModule() {
  moduleStatus.value = 'loading';
  moduleText.value = 'Preparing module for you ...';

  if (moduleIframe.value) {
    moduleIframe.value.remove();
  }

  moduleIframe.value = document.createElement('iframe');
  moduleIframe.value.style.opacity = 0;
  moduleIframe.value.allowfullscreen = true;
  moduleIframe.value.mozallowfullscreen = true;
  moduleIframe.value.webkitallowfullscreen = true;
  moduleBox.value.appendChild(moduleIframe.value);

  // TODO: offline version
  const OnlineOnly = Number(process.env.VUE_APP_ONLINE_ONLY || 0) === 1;

  // online version
  if (navigator.onLine || OnlineOnly) {
    // console.log(`${moduleUrl.value}/?hub-facilitator-portal-token=${SessionStore.token}`);
    // await this.$api.put('/facilitator-portal/sessions');
    moduleIframe.value.src = `${moduleUrl.value}/?hub-facilitator-portal-token=${SessionStore.token}`;
  }
  // offlineversion
  else {
    // Request an offline module url
    // For Nirojan:
    // Module downloader should store module urls ()
    moduleIframe.value.src = 'offline_module_url';
  }
}

/**
 * Module is ready on server
 * Create socket connection for tracking usage of the module
 */
function moduleReady(moduleIframeEvent) {
  if (!moduleIframeEvent.data || !moduleIframeEvent.data.status) {
    // moduleStatus.value = 'init';
    // moduleText.value = 'The module server is under maintenance.';
    return;
  }

  // Check the status of module that server returned
  if (moduleIframeEvent.data.status !== 'hub_module_ready') {
    moduleStatus.value = 'init';
    moduleText.value = 'The module is unavailable.';
    return;
  }

  moduleStatus.value = 'booting';
  moduleText.value = 'Establishing a new session ...';

  // TODO: offline version
  const OnlineOnly = Number(process.env.VUE_APP_ONLINE_ONLY || 0) === 1;

  // online version
  if (navigator.onLine || OnlineOnly) {
    // connect to socket server
    $socket.open();
    $socket.subscribe('connect', connected);
    $socket.subscribe('tracking', sessionCreated);
    $socket.subscribe('track_stopped', sessionRemoved);
    $socket.subscribe('socketFailed', socketFailed);
  }
  // offline version
  else {
    // store tracking data
    // For Ray to implement

    // {
    //   hostId: SessionStore.loginUser.id,
    //   topicId: props.topicId,
    //   moduleKey: currentModule.value.module_key,
    //   source: 'facilitator-portal',
    //   isContinue: moduleStatus.value === 'running',
    // }

    // generate a store key
    const offlineTrackingKey = '';

    Logger.debug(`Offline tracking: ${offlineTrackingKey}`);

    if (moduleStatus.value !== 'running') {
      moduleIframe.value.style.opacity = 1;
      moduleStatus.value = 'running';
      moduleText.value = `Running Module: <br />${currentModule.value.name}`;
    }
  }
}

/**
 * Request to end a running module
 */
function stopModule() {
  moduleStatus.value = 'stopping';
  moduleText.value = 'Completing module ...';

  endMethod.value = 'button';

  // Hide confirmation popup
  endPoupShow.value = false;
  holdWindow.value = false;

  // TODO: offline version
  const OnlineOnly = Number(process.env.VUE_APP_ONLINE_ONLY || 0) === 1;

  // online version
  if (navigator.onLine || OnlineOnly) {
    // remove current session on socket server
    $socket.emit('stop_track_module', {
      endMethod: endMethod.value,
    });
  }
  // offline version
  else {
    // Update offline tracking data
    // Operations
    // stop tracking data
    // For Ray to implement

    moduleStatus.value = 'stopped';
    $router.push({ name: 'moduleParticipation' });
  }
}

/**
 * Browser popup for leaving user
 */
function browserLeavePopup(e) {
  const event = e;
  event.preventDefault();
  event.returnValue = '';
}

function enterFullscreen() {
  if (moduleBox.value.requestFullscreen) {
    moduleBox.value.requestFullscreen();
  }
  if (moduleBox.value.webkitRequestFullscreen) {
    moduleBox.value.webkitRequestFullscreen();
  }
  if (moduleBox.value.mozRequestFullScreen) {
    moduleBox.value.mozRequestFullScreen();
  }
  if (moduleBox.value.msRequestFullscreen) {
    moduleBox.value.msRequestFullscreen();
  }
}

watch(moduleStatus, (val) => {
  if (val === 'loading') {
    window.addEventListener('beforeunload', browserLeavePopup);
    window.addEventListener('message', moduleReady, false);
  } else {
    if (['init', 'booting'].includes(val)) {
      window.removeEventListener('message', moduleReady);
    }
    if (['init', 'stopped'].includes(val)) {
      window.removeEventListener('beforeunload', browserLeavePopup);
    }
  }
});
</script>

<style scoped>
.module-launch {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.top-bar {
  left: 0;
  top: 0;
  margin: 0;
  padding: 0;
  width: 100%;
  box-shadow: 0 0 10px 0 #a7a9ac;
  background: #ffffff;
}

.top-bar-items {
  display: flex;
  justify-content: flex-start;
  align-items: center;

  padding: 0 0 0 40px;
  height: 100%;
}

.module-li {
  margin: 0.5em;
}
.module-btn {
  margin: 0 10px;
  padding: 10px 30px;
  border: 1px solid #a7a9ac;
  border-radius: 5px;
  background: #ffffff;
  color: #6d6e71;
  cursor: pointer;
}
.module-btn:not(.disabled):hover {
  border-color: transparent;
  box-shadow: 0 0 10px 0 #a7a9ac;
  background: #355c2e;
  color: #ffffff;
}
.module-btn.disabled {
  box-shadow: 0;
  background-color: #a7a9ac;
  color: #6d6e71;
  cursor: default;
}

.module-btn.icon-btn {
  padding: 10px 20px;
  display: flex;
  flex-direction: row;
}

.module-btn.module-btn.icon-btn > .svg-icon {
  margin-left: 0.5em;
}

.module-status {
  flex: 1;
  margin: 0 10px;
  font-size: 16px;
}

.module-launch-box {
  flex: 1;
  display: flex;
  flex-flow: column;
  justify-content: flex-end;

  width: 100%;
  height: 100%;
}
.module-launch-box > p {
  font-size: 2em;
  color: #333;
}
.module-launch-box :deep(iframe) {
  border: 0;
  width: 100%;
  height: 100%;
}
.module-launch-box .loading-cover {
  position: absolute;
  width: 100%;
  height: 100%;
  background: transparent;
}
.module-launch-box .lock-cover {
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 100%;
  background: #fff;
  opacity: 0.6;
}

.no-complete-bg {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 10001;
}
.no-complete-box {
  position: absolute;
  top: 50%;
  left: 50%;
  padding: 30px;
  width: 50%;
  max-width: 700px;
  transform: translate(-50%, -50%);
  background-color: #ffffff;
  border: 1px solid #a7a9ac;
  border-radius: 10px;
  display: flex;
  flex-flow: column;
  justify-content: center;

  color: #6d6e71;
  font-size: 20px;
}
.no-complete-box .no-complete-content {
  margin: 0 0 40px;
  padding: 0 20px;
  line-height: 40px;
  text-align: center;
}
.no-complete-box .no-complete-content.center {
  width: 100%;
  text-align: center;
  padding: 0;
}
.no-complete-box .no-complete-content span {
  margin: 0;
  padding: 0;
}
.no-complete-box .no-complete-content span.highlight {
  display: block;
  margin: 0;
  padding-bottom: 20px;
  text-align: center;
  font-size: 26px;
  color: #8ac640;
}

.operations {
  display: flex;
  justify-content: center;
}

.submit {
  margin: 0 20px;
  width: 30%;
  height: 40px;
  border: 1px solid #a7a9ac;
  border-radius: 5px;
  color: #6d6e71;
  font-size: 18px;
  font-weight: 400;
  outline: none;
  background-color: #8ac640;
  color: #ffffff;
  cursor: pointer;
}
.submit:hover {
  box-shadow: 0 0 5px 0 #a7a9ac;
}

/** Pads */
@media only screen and (max-width: 600px) {
  .top-bar-items {
    display: grid;
    justify-content: space-evenly;
    grid-template-columns: repeat(auto-fill, minmax(15%, 30%));

    box-sizing: border-box;
    height: 100%;
    padding: 0.5em;
  }

  .module-li {
    margin: 0;
  }
  .module-btn {
    margin: 0;
    padding: 0.5em;
    text-align: center;
  }
}
</style>
